<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, shallowRef } from 'vue'
import { useDarkMode } from '../composables/useDarkMode'
import Chart from 'chart.js/auto'

const props = defineProps({
  transactions: {
    type: Array,
    required: true
  },
  selectedMonth: {
    type: Number,
    required: true
  },
  selectedYear: {
    type: Number,
    required: true
  }
})

const { isDarkMode, currentTheme } = useDarkMode()
const chart = shallowRef(null)

function getChartColors() {
  const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement
  return [
    getComputedStyle(element).getPropertyValue('--chart-color-1').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-2').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-3').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-4').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-5').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-6').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-7').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-8').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-9').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-10').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-11').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-12').trim()
  ]
}

const categoryExpenses = computed(() => {
  const expenses = props.transactions.filter(t => {
    if (t.type !== 'debit') return false
    const transactionDate = new Date(t.date)
    return transactionDate.getMonth() === props.selectedMonth && 
           transactionDate.getFullYear() === props.selectedYear
  })
  
  const grouped = {}
  
  expenses.forEach(t => {
    const category = t.category || 'Uncategorized'
    if (!grouped[category]) {
      grouped[category] = 0
    }
    grouped[category] += t.amount
  })
  
  return Object.entries(grouped)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount)
})

const totalExpenses = computed(() =>
  categoryExpenses.value.reduce((sum, e) => sum + e.amount, 0)
)

const chartData = computed(() => {
  // Force recalculation when theme changes by accessing currentTheme
  const _ = currentTheme.value
  const labels = categoryExpenses.value.map(e => e.name)
  const data = categoryExpenses.value.map(e => e.amount)
  const colors = getChartColors()
  const backgroundColors = categoryExpenses.value.map((_, i) => colors[i % colors.length])
  return {
    labels,
    datasets: [{
      data,
      backgroundColor: backgroundColors,
      borderWidth: 2,
      hoverOffset: 10
    }]
  }
})

const chartCanvas = ref(null)

function getCSSVariableValue(variableName) {
  // Read from body element since data-theme attribute is applied there
  const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement
  const value = getComputedStyle(element).getPropertyValue(variableName).trim()
  return value
}

function createChart() {
  if (!chartCanvas.value) return
  const ctx = chartCanvas.value.getContext('2d')

  // Get the computed colors from CSS variables
  const legendColor = getCSSVariableValue('--light-text')
  const borderColor = getCSSVariableValue('--light-card')

  // Create chart data with fresh border color
  const data = {
    ...chartData.value,
    datasets: chartData.value.datasets.map(dataset => ({
      ...dataset,
      borderColor
    }))
  }

  chart.value = new Chart(ctx, {
    type: 'doughnut',
    data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '50%',
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            padding: 15,
            font: { size: 12 },
            color: legendColor,
            usePointStyle: true,
            pointStyle: 'circle',
            generateLabels: chart => {
              const data = chart.data
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const value = data.datasets[0].data[i]
                  const percentage = ((value / totalExpenses.value) * 100).toFixed(1)
                  return {
                    text: `${label}: $${value.toFixed(2)} (${percentage}%)`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    strokeStyle: 'transparent',
                    fontColor: legendColor,
                    lineWidth: 0,
                    pointStyle: 'circle',
                    hidden: false,
                    index: i
                  }
                })
              }
              return []
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#1364A3',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: context => {
              const label = context.label || ''
              const value = context.parsed
              const percentage = ((value / totalExpenses.value) * 100).toFixed(1)
              return `${label}: $${value.toFixed(2)} (${percentage}%)`
            }
          }
        }
      }
    }
  })
}

function updateChartData() {
  if (!chart.value) {
    nextTick(createChart)
    return
  }
  
  // Update chart data without recreating
  chart.value.data = chartData.value
  chart.value.update('none')
}

function recreateChart() {
  // Destroy existing chart
  if (chart.value) {
    chart.value.destroy()
    chart.value = null
  }
  
  // Wait for DOM to update with new theme before recreating
  nextTick(createChart)
}

watch(() => props.transactions, updateChartData, { deep: true })
watch(() => props.selectedMonth, updateChartData)
watch(() => props.selectedYear, updateChartData)
watch(currentTheme, recreateChart)

onMounted(() => {
  if (categoryExpenses.value.length > 0) createChart()
})

onBeforeUnmount(() => {
  if (chart.value) chart.value.destroy()
})
</script>

<template>
  <div class="category-expenses-card card h-100">
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-tags"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Spending by Category</h3>
        <p class="card-subtitle">Where your money goes</p>
      </div>
    </div>

    <div class="card-body mt-2">
      <div v-if="categoryExpenses.length > 0" class="chart-container">
        <canvas ref="chartCanvas" width="400" height="500"></canvas>
      </div>

      <div v-else class="empty-state text-center py-4">
        <i class="bi bi-tags display-4 text-muted mb-3"></i>
        <p class="text-muted mb-0">No spending to show yet</p>
        <small class="text-muted">Add categories to transactions to see where your money goes</small>
      </div>

      <div class="total-summary mt-3">
        <div class="row mb-2">
          <div class="col">
            <label class="form-label fw-semibold">
              <i class="fa-solid fa-dollar-sign me-1"></i>Total Spent This Month
            </label>
          </div>
        </div>
        <div class="row">
          <div class="col text-center">
            <span class="stat-value expense-value">${{ totalExpenses.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.total-summary {
  padding: 1rem;
  background: var(--nested-container-bg);
  border-radius: 8px;
}
</style>
